import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getLocalizedCaseStudies, useLanguage } from '../i18n';
import {
    FaArrowLeft,
    FaArrowRight,
    FaCheck,
    FaTags
} from 'react-icons/fa';
import { handleImageError } from '../utils/imageFallback';

const CaseStudies = () => {
    const { language, t } = useLanguage();
    const caseStudies = getLocalizedCaseStudies(language);
    const [selectedCase, setSelectedCase] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % caseStudies.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    return (
        <section id="case-studies" className="bg-paper py-24 lg:py-32">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        {t('caseTitleLead')}<span className="text-primary-600">{t('caseTitleAccent')}</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        {t('caseDescription')}
                    </p>
                </motion.div>

                {/* Featured Case Study Slider */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="mb-16 overflow-hidden border-y border-gray-200 bg-white"
                >
                    <div className="grid lg:grid-cols-2">
                        {/* Image Section */}
                        <div className="relative h-64 lg:h-auto">
                            <img
                                src={caseStudies[currentSlide].image}
                                alt={caseStudies[currentSlide].title}
                                className="w-full h-full object-cover"
                                onError={handleImageError}
                            />
                            <div className="absolute top-4 left-4">
                                <span className="border-l-2 border-primary-600 bg-white/90 px-3 py-1 text-sm font-medium text-primary-700">
                                    {caseStudies[currentSlide].category}
                                </span>
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="p-8 lg:p-12">
                            <h3 className="text-3xl font-bold text-gray-900 mb-2">
                                {caseStudies[currentSlide].title}
                            </h3>
                            <p className="text-primary-600 font-medium mb-4">
                                {caseStudies[currentSlide].client}
                            </p>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                {caseStudies[currentSlide].description}
                            </p>

                            {/* Technologies */}
                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                    <FaTags className="mr-2 text-primary-600" />
                                    {t('technologies')}
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {caseStudies[currentSlide].technologies.map((tech, index) => (
                                        <span
                                            key={index}
                                            className="border border-primary-200 px-3 py-1 text-sm font-medium text-primary-700"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Results */}
                            <div className="mb-8">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                    <FaCheck className="mr-2 text-green-500" />
                                    {t('keyResults')}
                                </h4>
                                <ul className="space-y-2">
                                    {caseStudies[currentSlide].results.map((result, index) => (
                                        <li key={index} className="flex items-start space-x-3">
                                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                            </div>
                                            <span className="text-gray-700">{result}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center justify-between p-6 bg-gray-50">
                        <button
                            onClick={prevSlide}
                            className="flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors"
                        >
                            <FaArrowLeft />
                            <span>Trước</span>
                        </button>

                        <div className="flex space-x-2">
                            {caseStudies.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-primary-600' : 'bg-gray-300'
                                        }`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={nextSlide}
                            className="flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors"
                        >
                            <span>{t('next')}</span>
                            <FaArrowRight />
                        </button>
                    </div>
                </motion.div>

                {/* All Case Studies Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                    {caseStudies.map((caseStudy, index) => (
                        <motion.div
                            key={caseStudy.id}
                            variants={itemVariants}
                            className="group cursor-pointer overflow-hidden border border-gray-200 bg-white transition-colors duration-300 hover:border-primary-300"
                            onClick={() => setSelectedCase(caseStudy)}
                        >
                            <div className="relative overflow-hidden">
                                <img
                                    src={caseStudy.image}
                                    alt={caseStudy.title}
                                    className="h-48 w-full object-cover transition-opacity duration-300 group-hover:opacity-90"
                                    onError={handleImageError}
                                />
                                <div className="absolute top-4 left-4">
                                    <span className="border-l-2 border-primary-600 bg-white/90 px-3 py-1 text-sm font-medium text-primary-700">
                                        {caseStudy.category}
                                    </span>
                                </div>
                            </div>

                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                                    {caseStudy.title}
                                </h3>
                                <p className="text-primary-600 font-medium text-sm mb-3">
                                    {caseStudy.client}
                                </p>
                                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                                    {caseStudy.description}
                                </p>

                                <div className="flex items-center justify-between">
                                    <div className="flex -space-x-1">
                                        {caseStudy.technologies.slice(0, 3).map((tech, techIndex) => (
                                            <div
                                                key={techIndex}
                                                className="border border-primary-200 px-2 py-1 text-xs font-medium text-primary-700"
                                                title={tech}
                                            >
                                                {tech.substring(0, 2)}
                                            </div>
                                        ))}
                                        {caseStudy.technologies.length > 3 && (
                                            <div className="border border-gray-200 px-2 py-1 text-xs text-gray-600">
                                                +{caseStudy.technologies.length - 3}
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Case Study Modal */}
                <AnimatePresence>
                    {selectedCase && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                            onClick={() => setSelectedCase(null)}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="relative">
                                    <img
                                        src={selectedCase.image}
                                        alt={selectedCase.title}
                                        className="w-full h-64 object-cover"
                                        onError={handleImageError}
                                    />
                                    <button
                                        onClick={() => setSelectedCase(null)}
                                        className="absolute top-4 right-4 bg-white text-gray-600 hover:text-gray-800 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                                    >
                                        ×
                                    </button>
                                </div>

                                <div className="p-8">
                                    <div className="flex items-start justify-between mb-6">
                                        <div>
                                            <h3 className="text-3xl font-bold text-gray-900 mb-2">
                                                {selectedCase.title}
                                            </h3>
                                            <p className="text-primary-600 font-medium text-lg">
                                                {selectedCase.client}
                                            </p>
                                        </div>
                                        <span className="border-l-2 border-primary-600 px-4 py-2 font-medium text-primary-700">
                                            {selectedCase.category}
                                        </span>
                                    </div>

                                    <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                                        {selectedCase.description}
                                    </p>

                                    <div className="grid md:grid-cols-2 gap-8">
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-4 text-xl">{t('technologies')}</h4>
                                            <div className="flex flex-wrap gap-2 mb-6">
                                                {selectedCase.technologies.map((tech, index) => (
                                                    <span
                                                        key={index}
                                                        className="border border-primary-200 px-3 py-2 font-medium text-primary-700"
                                                    >
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-4 text-xl">{t('keyResults')}</h4>
                                            <ul className="space-y-3">
                                                {selectedCase.results.map((result, index) => (
                                                    <li key={index} className="flex items-start space-x-3">
                                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                                                            <FaCheck className="text-green-500 text-sm" />
                                                        </div>
                                                        <span className="text-gray-700 text-lg">{result}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                                        <motion.button
                                            onClick={() => {
                                                setSelectedCase(null);
                                                const element = document.getElementById('contact');
                                                if (element) element.scrollIntoView({ behavior: 'smooth' });
                                            }}
                                            className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors"
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            {t('startProject')}
                                        </motion.button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default CaseStudies;
